// Copyright (c) 2020 WiseTime. All rights reserved.

import React, {
  useRef,
  useState,
} from "react"
import { useDebounce } from "react-use"
import styled, { css, SimpleInterpolation } from "styled-components/macro"
import Tooltip from "features/shared/Tooltip"
import {
  PostStatus,
  TrashStatus
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import unknownIcon from "resources/appicons/unknown.png"
import { BreakWord } from "../../shared/styles"
import CopyToClipboard from "../../shared/CopyToClipboard"
import { ROW_COPY_TO_CLIPBOARD_CLASSNAME } from "../../shared/classNames"

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`

const Info = styled.div<{ isGroup: boolean, timeGroupPostStatus?: PostStatus }>`
  display: flex;
  position: relative;
  padding: .9rem 0;
  ${({ isGroup }): SimpleInterpolation => {
    if (isGroup) {
      return css`
        margin-left: 16px;
      `
    }
    return ""
  }}
  ${({ timeGroupPostStatus }): SimpleInterpolation => timeGroupPostStatus === PostStatus.SUCCESS ? css`
    filter: saturate(0);
  ` : ""}
`
const Icon = styled.img`
  width: 1.6rem;
  min-width: 1.6rem;
  height: 1.6rem;
  display: inline-block;
  vertical-align: top;
`

const LabelTooltip = styled(Tooltip)`
  && {
    ${BreakWord};
    top: 0;
    left: 3.8rem;
    max-width: 100%;
    white-space: normal;
    display: inline-block;
    z-index: -10;
    opacity: 0;
  }
`

const Label = styled.span<{
  timeGroupPostStatus?: PostStatus,
  pendingStatus?: TrashStatus,
}>`
  font-size: 1.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 1rem 0 .9rem 1.4rem;
  margin-right: .5rem;
  
  &::after {
    // suppress Safari native title behaviour https://stackoverflow.com/a/43915246
    content: "";
    display: block;
  }
  
  ${({ timeGroupPostStatus, pendingStatus, theme }): SimpleInterpolation => {
    if (timeGroupPostStatus === PostStatus.SUCCESS || timeGroupPostStatus === PostStatus.TRANSIENT_FAILURE || timeGroupPostStatus === PostStatus.PENDING) {
      return css`
        color: ${theme.colors.nero};
      `
    }
    if (pendingStatus === TrashStatus.TRASHED) {
      return css`
        color: ${theme.colors.lightCoral};
        text-decoration: line-through;
      `
    }
    return css`
      color: ${theme.colors.nero};
    `
  }}

  &:hover {
    [data-tooltip] {
      transition-delay: .7s;
      opacity: 1;
      z-index: 2;
    }
  }
}
`

type Props = {
  activity: string,
  description: string,
  iconUrl?: string,
  isGroup?: boolean,
  timeGroupPostStatus?: PostStatus,
  pendingStatus?: TrashStatus,
  showTooltip?: boolean,
  isDragging?: boolean,
  displayCopyToClipboard?: boolean,
}

const fallbackIconUrl = "https://cf.wisetime.com/icon-cache/prod/appicon/unknown_32x32.png"

const TimeRowIconDesc = ({
  activity,
  description,
  iconUrl = "",
  isGroup = false,
  timeGroupPostStatus = undefined,
  pendingStatus = undefined,
  showTooltip = false,
  isDragging = false,
  displayCopyToClipboard = true,
}: Props): React.ReactElement<Props> => {
  if (iconUrl === "") {
    // If no icon URL is given, use the fallback app icon
    iconUrl = fallbackIconUrl
  }

  const labelRef = useRef<HTMLSpanElement>(null)
  const [isOverflowed, setIsOverflowed] = useState(false)
  const [debouncedIsOverflowed, debouncedSetIsOverflowed] = useState(false)
  const onMouseOver = (): void => {
    if (!showTooltip) {
      return
    }
    debouncedSetIsOverflowed(labelRef.current != null && labelRef.current.offsetWidth < labelRef.current.scrollWidth)
  }
  // debounce ms delay should match transition-delay for tooltip from time row
  useDebounce((): void => {
    if (!showTooltip) {
      return
    }
    setIsOverflowed(debouncedIsOverflowed)
  }, debouncedIsOverflowed ? 700 : 0, [debouncedIsOverflowed])

  // when both iconUrl and fallbackIconUrl are not available use unknownIcon
  const addDefaultSrc = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    event.currentTarget.src = unknownIcon
  }

  return (
    <Wrapper>
      <Info isGroup={isGroup} timeGroupPostStatus={timeGroupPostStatus}>
        <Icon
          src={iconUrl}
          onError={addDefaultSrc}
          width={16}
          height={16}
          alt={activity}
          title={activity}
        />
      </Info>
      <Label
        ref={labelRef}
        timeGroupPostStatus={timeGroupPostStatus}
        pendingStatus={pendingStatus}
        onMouseOver={onMouseOver}
        title={!showTooltip ? description : undefined}
      >
        {isOverflowed && !isDragging && <LabelTooltip text={description}/>}
        {description}
      </Label>
      {displayCopyToClipboard && (
        <CopyToClipboard className={ROW_COPY_TO_CLIPBOARD_CLASSNAME} text={description}/>
      )}
    </Wrapper>
  )
}

export default TimeRowIconDesc
